import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";

// Radix Select não aceita SelectItem com value "", então usamos um sentinela
// para a opção "limpar / não definido".
const VALOR_VAZIO = "__none__";

/**
 * Faz o parse do campo `opcoes_info_compl` (JSON) em um mapa
 * `{ "1": ["A","B"], "3": ["X"] }`. Índices sem opções (ou vazios) são omitidos.
 * Tolerante a JSON inválido — retorna `{}` nesse caso.
 */
export function parseOpcoesInfoCompl(
  raw?: string | null,
): Record<string, string[]> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, string[]> = {};
    for (const chave of Object.keys(parsed)) {
      const arr = (parsed as Record<string, unknown>)[chave];
      if (!Array.isArray(arr)) continue;
      const limpas = arr
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0);
      if (limpas.length > 0) out[chave] = limpas;
    }
    return out;
  } catch {
    return {};
  }
}

/** Retorna as opções configuradas para uma info complementar (ou []). */
export function getOpcoesInfoCompl(
  impressaoConfig: ImpressaoConfigResponse | null | undefined,
  idx: number,
): string[] {
  const mapa = parseOpcoesInfoCompl(impressaoConfig?.opcoes_info_compl);
  return mapa[String(idx)] ?? [];
}

/**
 * Retorna `{ idx: opcao }` para as info. complementares que têm exatamente uma
 * opção pré-definida cadastrada — nesses casos não há escolha a fazer, então o
 * valor já entra selecionado nos modos de impressão.
 */
export function getPreDefinicoesUnicas(
  impressaoConfig: ImpressaoConfigResponse | null | undefined,
): Record<string, string> {
  const mapa = parseOpcoesInfoCompl(impressaoConfig?.opcoes_info_compl);
  const out: Record<string, string> = {};
  for (const chave of Object.keys(mapa)) {
    if (mapa[chave].length === 1) out[chave] = mapa[chave][0];
  }
  return out;
}

/**
 * Preenche os `inf_compl_N` vazios do objeto com a única opção pré-definida do
 * índice (ver `getPreDefinicoesUnicas`). Só toca em chaves que já existem no
 * objeto e nunca sobrescreve valor preenchido (XML, planilha, ERP, categoria).
 * Devolve o próprio objeto quando não há nada a preencher — assim dá pra usar
 * dentro de `useEffect` sem entrar em loop de render.
 */
export function aplicarPreDefinicoesUnicas<T extends object>(
  impressaoConfig: ImpressaoConfigResponse | null | undefined,
  valores: T,
): T {
  if (!valores) return valores;
  const unicas = getPreDefinicoesUnicas(impressaoConfig);
  let alterado = false;
  const out = { ...valores } as Record<string, unknown>;
  for (const idx of Object.keys(unicas)) {
    const campo = `inf_compl_${idx}`;
    if (!(campo in out)) continue;
    const atual = out[campo];
    if (atual != null && String(atual).trim() !== "") continue;
    out[campo] = unicas[idx];
    alterado = true;
  }
  return alterado ? (out as T) : valores;
}

/**
 * Faz o parse do campo `info_compl_obrigatorios` (JSON, ex.: `[1,3,5]`) em uma
 * lista de índices (1..10). Tolerante a JSON inválido — retorna `[]` nesse caso.
 */
export function parseInfoComplObrigatorios(raw?: string | null): number[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 10);
  } catch {
    return [];
  }
}

/** Indica se a info complementar `idx` está marcada como obrigatória. */
export function isInfoComplObrigatorio(
  impressaoConfig: ImpressaoConfigResponse | null | undefined,
  idx: number,
): boolean {
  return parseInfoComplObrigatorios(
    impressaoConfig?.info_compl_obrigatorios,
  ).includes(idx);
}

/** Tipos de valor possíveis para uma info complementar. */
export type TipoInfoCompl =
  | "texto"
  | "numero"
  | "monetario"
  | "data"
  | "datahora";

export const TIPO_INFO_COMPL_PADRAO: TipoInfoCompl = "texto";

/** Rótulos amigáveis para cada tipo (usados no seletor de configuração). */
export const TIPOS_INFO_COMPL: { valor: TipoInfoCompl; label: string }[] = [
  { valor: "texto", label: "Texto" },
  { valor: "numero", label: "Número" },
  { valor: "monetario", label: "Monetário" },
  { valor: "data", label: "Data" },
  { valor: "datahora", label: "Data e hora" },
];

const TIPOS_VALIDOS = new Set<string>(TIPOS_INFO_COMPL.map((t) => t.valor));

/**
 * Faz o parse do campo `tipos_info_compl` (JSON, ex.: `{"1":"numero"}`) em um
 * mapa `{ "1": "numero" }`. Valores desconhecidos ou `"texto"` (padrão) são
 * omitidos. Tolerante a JSON inválido — retorna `{}` nesse caso.
 */
export function parseTiposInfoCompl(
  raw?: string | null,
): Record<string, TipoInfoCompl> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, TipoInfoCompl> = {};
    for (const chave of Object.keys(parsed)) {
      const valor = String((parsed as Record<string, unknown>)[chave]);
      if (TIPOS_VALIDOS.has(valor) && valor !== TIPO_INFO_COMPL_PADRAO) {
        out[chave] = valor as TipoInfoCompl;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Retorna o tipo configurado para uma info complementar (padrão: "texto"). */
export function getTipoInfoCompl(
  impressaoConfig: ImpressaoConfigResponse | null | undefined,
  idx: number,
): TipoInfoCompl {
  const mapa = parseTiposInfoCompl(impressaoConfig?.tipos_info_compl);
  return mapa[String(idx)] ?? TIPO_INFO_COMPL_PADRAO;
}

/**
 * Formata o valor de uma info complementar para impressão/exibição.
 * Os inputs nativos de data gravam em ISO (`AAAA-MM-DD` / `AAAA-MM-DDTHH:mm`);
 * na etiqueta o valor deve sair como `DD/MM/AAAA` (+ ` HH:mm` para datahora).
 * Valores que não estejam no formato ISO são retornados como estão.
 */
export function formatarValorInfoCompl(
  tipo: TipoInfoCompl,
  valor: string,
): string {
  if (!valor || (tipo !== "data" && tipo !== "datahora")) return valor;
  const m = valor.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?)?$/,
  );
  if (!m) return valor;
  const data = `${m[3]}/${m[2]}/${m[1]}`;
  return m[4] ? `${data} ${m[4]}:${m[5]}` : data;
}

/** Mapeia o tipo para o `type` do input HTML e atributos auxiliares. */
function atributosInput(tipo: TipoInfoCompl): {
  type: string;
  inputMode?: "numeric" | "decimal";
  step?: string;
} {
  switch (tipo) {
    case "numero":
      return { type: "number", inputMode: "numeric", step: "1" };
    case "monetario":
      return { type: "number", inputMode: "decimal", step: "0.01" };
    case "data":
      return { type: "date" };
    case "datahora":
      return { type: "datetime-local" };
    default:
      return { type: "text" };
  }
}

/**
 * Dado um objeto com campos `inf_compl_1..10`, retorna os índices das info.
 * complementares obrigatórias que estão vazias. Lista vazia = tudo preenchido.
 */
export function infoComplObrigatoriasFaltando(
  impressaoConfig: ImpressaoConfigResponse | null | undefined,
  valores: Record<string, unknown> | null | undefined,
): number[] {
  const obrigatorias = parseInfoComplObrigatorios(
    impressaoConfig?.info_compl_obrigatorios,
  );
  return obrigatorias.filter((idx) => {
    const v = valores ? (valores as Record<string, unknown>)[`inf_compl_${idx}`] : undefined;
    return v == null || String(v).trim() === "";
  });
}

type InfoComplFieldProps = {
  /** Índice da info complementar (1..10). */
  idx: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  className?: string;
  /**
   * Permite passar as opções diretamente (ex.: pré-visualização nas
   * configurações). Quando omitido, lê de `impressaoConfig.opcoes_info_compl`.
   */
  opcoesOverride?: string[];
  /**
   * Permite passar o tipo diretamente. Quando omitido, lê de
   * `impressaoConfig.tipos_info_compl` (padrão: "texto").
   */
  tipoOverride?: TipoInfoCompl;
};

/**
 * Campo de informação complementar. Quando há opções configuradas para o
 * índice (via aba de configurações → `opcoes_info_compl`), renderiza um
 * dropdown somente-seleção (escrita desativada). Caso contrário, mantém o
 * comportamento normal de input de texto livre.
 */
export function InfoComplField({
  idx,
  value,
  onChange,
  disabled,
  id,
  placeholder,
  className,
  opcoesOverride,
  tipoOverride,
}: InfoComplFieldProps) {
  const { impressaoConfig } = useImpressaoConfig();
  const opcoes = opcoesOverride ?? getOpcoesInfoCompl(impressaoConfig, idx);
  const tipo = tipoOverride ?? getTipoInfoCompl(impressaoConfig, idx);

  // Sem opções → input livre respeitando o tipo configurado.
  if (opcoes.length === 0) {
    const attrs = atributosInput(tipo);
    return (
      <Input
        autoComplete="off"
        disabled={disabled}
        id={id}
        type={attrs.type}
        inputMode={attrs.inputMode}
        step={attrs.step}
        placeholder={placeholder}
        className={className}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // Preserva um valor já gravado que não esteja na lista de opções.
  const lista = value && !opcoes.includes(value) ? [value, ...opcoes] : opcoes;

  return (
    <Select
      disabled={disabled}
      value={value ? value : VALOR_VAZIO}
      onValueChange={(v) => onChange(v === VALOR_VAZIO ? "" : v)}
    >
      <SelectTrigger id={id} className={className ?? "w-full"}>
        <SelectValue placeholder={placeholder ?? "Selecione"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={VALOR_VAZIO}>—</SelectItem>
        {lista.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {opt}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
